import { palette } from '@client/theme/colors';
import { radii, theme } from '@client/theme/constants';
import { typography } from '@client/theme/typography';
import type { ThemeOptions } from '@mui/material';

declare module '@mui/material/Alert' {
  interface AlertPropsVariantOverrides {
    default: true;
  }
}

const TextField: ThemeOptions['components'] = {
  MuiFormControl: {
    styleOverrides: {
      root: {
        zIndex: 'auto',
      },
      marginDense: {
        marginBottom: 0,
        marginTop: 0,
        display: 'flex',

        '&:not(:first-child)': {
          marginTop: theme.spacing(1),
        },

        // If the field is followed by a button or by a div (usually a Stack)
        '&:last-of-type + button, &:last-of-type + section': {
          marginTop: theme.spacing(3),
        },
      },
      marginNormal: {
        marginBottom: 0,
        marginTop: 0,

        '&:not(:first-child)': {
          marginTop: theme.spacing(3),
        },

        // If the field is followed by a button or by a div (usually a Stack)
        '&:last-of-type + button, &:last-of-type + section': {
          marginTop: theme.spacing(2),
        },
      },
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      root: {
        marginLeft: 0,

        '&.Mui-disabled': {
          pointerEvents: 'none',
        },
      },
      label: {
        ...typography.overline,
        fontWeight: 700,
        display: 'inline-flex',
        color: palette.text.secondary,
      },
      labelPlacementStart: {
        marginLeft: 0,
        marginRight: 0,

        '& > span': {
          fontWeight: 700,
        },
      },
    },
    variants: [
      {
        props: { labelPlacement: 'end' },
        style: {
          marginRight: 0,
        },
      },
    ],
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        color: palette.text.disabled,
        marginLeft: 0,
        marginTop: theme.spacing(1 / 2),
        marginRight: 0,
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        ...typography.body2,
        marginTop: '0 !important',
        color: palette.grey[600],
        height: 20,

        '&$focused': {
          color: palette.grey[600],
        },

        '&$error': {
          color: palette.grey[600],
        },

        '& + [role="radiogroup"]': {
          marginTop: theme.spacing(1),
        },
      },
      asterisk: {
        color: palette.text.disabled,

        '&$error': {
          color: palette.error.main,
        },
      },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: {
        color: palette.grey[400],
        fontSize: 24,

        '& > .MuiIconButton-root': {
          minHeight: 24,
          minWidth: 24,
        },

        '& > svg': {
          fontSize: 24,
        },
      },
      standard: {
        marginBottom: theme.spacing(1 / 2),

        '&.MuiInputAdornment-positionStart': {
          marginRight: theme.spacing(1 / 2),
        },

        '&.MuiInputAdornment-positionEnd': {
          marginLeft: theme.spacing(1 / 2),
        },

        '&.MuiInputAdornment-sizeSmall': {
          height: 20,
          width: 20,
          fontSize: 20,

          '& > svg': {
            fontSize: 20,
          },
        },
      },
      outlined: {
        zIndex: 1,

        '&.MuiInputAdornment-positionStart': {
          marginLeft: theme.spacing(1),

          '& + input, & + textarea': {
            paddingLeft: 0,
          },
        },

        '&.MuiInputAdornment-positionEnd': {
          marginRight: theme.spacing(1),
        },
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        '&:after': {
          borderBottomWidth: 1,
          borderBottomColor: palette.grey[500],
        },

        '&.Mui-focused:after': {
          borderBottomWidth: 1,
          borderBottomColor: palette.text.secondary,
        },
      },
      input: {
        padding: 0,
        paddingBottom: theme.spacing(1 / 2),
        fontSize: typography.subtitle1.fontSize,
        lineHeight: typography.subtitle1.lineHeight,
        height: 24,
      },
      inputSizeSmall: {
        padding: 0,
        paddingBottom: theme.spacing(1 / 2),
        fontSize: typography.body1.fontSize,
        lineHeight: typography.body1.lineHeight,
        height: 20,
      },
      inputMultiline: {
        padding: 0,
        paddingBottom: theme.spacing(1 / 2),
        minHeight: theme.spacing(2),
      },
      multiline: {
        padding: 0,
      },
    },
  },
  MuiInputLabel: {
    defaultProps: {
      shrink: true,
    },
    styleOverrides: {
      root: {
        ...typography.body2,
        marginTop: '0 !important',

        '&.Mui-focused': {
          color: palette.text.secondary,
        },

        '& + .MuiInputBase-root': {
          marginTop: theme.spacing(1),
        },
      },
      shrink: {
        marginTop: theme.spacing(1),
        transform: 'none', // override translate() and scale()
        position: 'static !important' as any,
        color: palette.text.secondary,
      },
      sizeSmall: {
        ...typography.body2,
      },
    },
  },
  MuiFilledInput: {
    defaultProps: {
      disableUnderline: true,
    },
    styleOverrides: {
      root: {
        borderRadius: radii.large,

        '& .MuiInputAdornment-root': {
          color: palette.grey[500],
        },
      },
      input: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
      },
    },
  },
  MuiOutlinedInput: {
    defaultProps: {
      notched: false,
    },
    styleOverrides: {
      root: {
        padding: 0,

        '& fieldset > legend': {
          display: 'none',
        },

        '& > .MuiInputAdornment-root': {
          zIndex: 1,

          '&.MuiInputAdornment-positionStart': {
            marginLeft: theme.spacing(1),

            '& + input, & + textarea': {
              paddingLeft: 0,
            },
          },

          '&.MuiInputAdornment-positionEnd': {
            marginRight: theme.spacing(1),
          },
        },
      },
      input: {
        padding: theme.spacing(1),
        zIndex: 1,
      },
      notchedOutline: {
        padding: theme.spacing(0, 1),
        top: 0,
        borderRadius: radii.medium,
        borderColor: palette.grey[500],
        backgroundColor: palette.background.paper,
        zIndex: 0,
      },
    },
  },
  MuiNativeSelect: {
    styleOverrides: {
      select: {
        zIndex: 1,
      },
      icon: {
        fontSize: 24,
      },
    },
  },
  MuiSelect: {
    defaultProps: {
      MenuProps: {
        elevation: 0,
        PaperProps: {
          elevation: 0,
          square: true,
        },
      },
    },
    styleOverrides: {
      select: {
        minHeight: 20,
      },
      multiple: {
        '& > .MuiCheckbox-root': {
          display: 'none',
        },
        '& > .MuiListItemText-root': {
          display: 'inline',
        },
      },
      icon: {
        color: `${palette.text.disabled} !important`,
        fontSize: 24,
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      margin: 'normal',
      variant: 'standard',
    },
    styleOverrides: {
      root: {
        '&::placeholder': {
          color: palette.text.disabled,
        },
        '&::-moz-placeholder': {
          color: palette.text.disabled,
        },
        '&:-ms-input-placeholder': {
          color: palette.text.disabled,
        },
        '&::-webkit-input-placeholder': {
          color: palette.text.disabled,
        },
      },
    },
  },
};

export default TextField;
